import JSZip from 'jszip';

/**
 * «Ajustar texto» en las columnas que traen varias líneas.
 *
 * ── Por qué hace falta esto ────────────────────────────────────────────────
 *
 * Un recorrido de varias carreteras trae una línea por vía en las columnas
 * Carretera / Pk inicial / Pk final. Ese salto de línea **sí llega al fichero**
 * —medido sobre el `.xlsx` generado: la celda contiene `C-25\nC-25z`—, pero
 * Excel solo DIBUJA un salto embebido si la celda tiene activado «Ajustar
 * texto». Sin él lo pinta todo en una línea, por mucha altura que tenga la
 * fila.
 *
 * Y ese ajuste es un ESTILO, que la edición comunitaria de SheetJS no escribe.
 * Comprobado contra la 0.20.3 (la que usa `main`): pidiéndole
 * `cell.s = { alignment: { wrapText: true } }` con `cellStyles: true`, el
 * `styles.xml` sale con cero `wrapText` y las celdas sin atributo `s`. Sí
 * escribe anchos (`!cols`) y alturas (`!rows`), que no sirven para esto.
 *
 * ── Por qué se parchea el fichero y no se cambia de librería ───────────────
 *
 * Las alternativas eran un fork sin mantenimiento (`xlsx-js-style`, último
 * commit hace cuatro años) o combinar celdas con `!merges`, que se ve bien pero
 * convierte un recorrido en varias filas y deja el Excel hostil para filtrar y
 * para tablas dinámicas.
 *
 * Esto no toca la librería: coge el fichero que ACABA de escribir SheetJS —un
 * `.xlsx` es un ZIP— y le añade **un** estilo. `jszip` ya es dependencia
 * directa nuestra, así que no entra nada nuevo.
 *
 * ── Y por qué se puede confiar ─────────────────────────────────────────────
 *
 * No se parsea un fichero ajeno: es el nuestro, generado un instante antes, con
 * un `styles.xml` de ~1 kB y forma fija. Aun así, **si algo no encaja se
 * devuelve el fichero tal cual** — el peor caso es el comportamiento de hoy,
 * nunca un fichero corrupto.
 */

/** Estilo nuevo: envolver y pegar el texto arriba de la celda. */
const WRAP_XF =
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1">' +
  '<alignment wrapText="1" vertical="top"/></xf>';

export interface WrapTextOptions {
  /** Índices (0 = primera) de las columnas a envolver. */
  columns: number[];
  /** Primera fila de datos, 1-indexada. Por defecto 2: la 1 es la cabecera. */
  fromRow?: number;
  /** Hoja a tocar, 1-indexada. Por defecto la primera. */
  sheet?: number;
}

/** 0 → A, 25 → Z, 26 → AA. */
export function columnLetter(index: number): string {
  let n = index;
  let letra = '';
  do {
    letra = String.fromCharCode(65 + (n % 26)) + letra;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return letra;
}

/**
 * Devuelve el mismo `.xlsx` con «Ajustar texto» en las columnas indicadas.
 *
 * Si no hay columnas que envolver, o el fichero no tiene la forma esperada,
 * devuelve los bytes recibidos sin tocarlos.
 */
export async function applyWrapText(
  bytes: ArrayBuffer | Uint8Array,
  { columns, fromRow = 2, sheet = 1 }: WrapTextOptions
): Promise<ArrayBuffer | Uint8Array> {
  if (columns.length === 0) return bytes;

  try {
    const zip = await JSZip.loadAsync(bytes);
    const stylesFile = zip.file('xl/styles.xml');
    const sheetPath = `xl/worksheets/sheet${sheet}.xml`;
    const sheetFile = zip.file(sheetPath);
    if (!stylesFile || !sheetFile) return bytes;

    // 1. Un `<xf>` más al final de `cellXfs`; su índice es el que había.
    const styles = await stylesFile.async('string');
    const cellXfs = styles.match(/<cellXfs count="(\d+)">/);
    if (!cellXfs || !styles.includes('</cellXfs>')) return bytes;
    const wrapIndex = Number(cellXfs[1]);
    zip.file(
      'xl/styles.xml',
      styles
        .replace(/<cellXfs count="\d+">/, `<cellXfs count="${wrapIndex + 1}">`)
        .replace('</cellXfs>', `${WRAP_XF}</cellXfs>`)
    );

    // 2. Ese estilo, en las celdas de esas columnas.
    //
    // Se marcan las celdas y no la columna entera a propósito: en OOXML el
    // estilo propio de la celda manda sobre el de la columna, y SheetJS las
    // escribe SIN atributo `s` — o sea, con el estilo 0. Marcar solo la columna
    // no las alcanzaría.
    const letras = new Set(columns.map(columnLetter));
    const hoja = await sheetFile.async('string');
    zip.file(
      sheetPath,
      hoja.replace(
        /<c r="([A-Z]+)(\d+)"/g,
        (todo, col: string, fila: string) =>
          letras.has(col) && Number(fila) >= fromRow
            ? `<c r="${col}${fila}" s="${wrapIndex}"`
            : todo
      )
    );

    return await zip.generateAsync({
      type: 'uint8array',
      compression: 'DEFLATE',
    });
  } catch {
    // Un fichero sin envolver se lee; uno a medio escribir, no.
    return bytes;
  }
}

/** Índices de las columnas cuyo contenido trae saltos de línea. */
export function columnsWithLineBreaks(rows: string[][]): number[] {
  const columnas = new Set<number>();
  for (const fila of rows) {
    fila.forEach((celda, i) => {
      if (typeof celda === 'string' && celda.includes('\n')) columnas.add(i);
    });
  }
  return [...columnas].sort((a, b) => a - b);
}

export default applyWrapText;
